import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Compass, LineChart, KeyRound, Landmark } from "lucide-react";
import SEO from "@/components/SEO";
import Marquee from "@/components/Marquee";
import Reveal, { MaskedLine } from "@/components/Reveal";
import ProjectCard from "@/components/ProjectCard";
import CTABand from "@/components/CTABand";
import { opportunities } from "@/data/projects";
import { destinations } from "@/data/destinations";
import { testimonials } from "@/data/testimonials";

const HERO_IMG = "https://images.unsplash.com/flagged/photo-1559717865-a99cac1c95d8?crop=entropy&cs=srgb&fm=jpg&q=85";

const STATS = [
  { value: "Prime global demand", label: "International buyers across established UAE districts" },
  { value: "Attractive yields", label: "Rental potential across selected UAE districts" },
  { value: "10-year", label: "Golden Visa access through qualifying property" },
  { value: "0%", label: "Capital gains and income tax exemption on property" },
];

const WHY = [
  {
    icon: Compass,
    title: "Lifestyle-led shortlisting",
    text: "We start with the life you want to live — school runs, winters in the sun, a base between continents — and shortlist homes around it, not the other way round.",
  },
  {
    icon: LineChart,
    title: "Market intelligence",
    text: "Net-yield breakdowns, developer track records and district-level evidence underpin every recommendation we make. Numbers first, adjectives second.",
  },
  {
    icon: KeyRound,
    title: "Brokerage access",
    text: "Launch allocations, off-market introductions and developer relationships across Dubai, Abu Dhabi and Al Marjan Island — access that portals cannot offer.",
  },
];

const PARTNERS = [
  { name: "BNW Developments", logo: "/images/logos/bnw.png", square: true },
  { name: "Emaar", logo: "/images/logos/emaar.svg", invert: true },
  { name: "Nakheel", logo: "/images/logos/nakheel.svg", invert: true },
  { name: "OMNIYAT", logo: "/images/logos/omniyat.svg" },
  { name: "Sobha Realty", logo: "/images/logos/sobha.svg" },
  { name: "Aldar", logo: "/images/logos/aldar.png", square: true },
  { name: "Binghatti", logo: "/images/logos/binghatti.svg" },
  { name: "DAMAC", logo: "/images/logos/damac.svg", invert: true },
  { name: "Select Group", logo: "/images/logos/selectgroup.svg", invert: true },
  { name: "Marriott", logo: "/images/logos/marriott.png" },
  { name: "Dorchester Collection", logo: "/images/logos/dorchester.svg", square: true },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="home-page">
      <SEO
        title="Dubai Property Investment for UK Buyers | ACRE"
        description="Explore curated Dubai and UAE property with ACRE. Luxury homes, branded residences and investment opportunities for UK and international buyers."
        path="/"
      />

      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden grain" data-testid="home-hero">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <motion.img
            src={HERO_IMG}
            alt="Dubai skyline at dusk — Dubai property investment for UK buyers"
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/60 to-[#0B0C10]/40" />
        <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-48 w-full">
          <motion.p
            className="eyebrow mb-6"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            ACRE — Al Casa Real Estate · Dubai · Abu Dhabi · Ras Al Khaimah
          </motion.p>
          <div className="font-display text-5xl sm:text-6xl lg:text-7xl font-light text-[#F4F3EF] leading-[1.04] max-w-5xl" role="presentation">
            <MaskedLine delay={0.45}>Live the lifestyle.</MaskedLine>
            <MaskedLine delay={0.6}>
              <span className="italic text-gold-gradient">Own the address.</span>
            </MaskedLine>
          </div>
          <motion.h1
            className="font-display text-2xl sm:text-3xl font-light text-[#E6CA65] mt-7 tracking-wide"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
          >
            Dubai Property Investment for UK Buyers
          </motion.h1>
          <motion.p
            className="text-lg text-[#c9cdd6] font-light leading-relaxed max-w-2xl mt-5"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
          >
            Curated Dubai property and Dubai real estate for UK buyers and international investors — UAE property
            investment spanning branded residences, Palm Jumeirah villas and Al Marjan Island's new beachfront story,
            chosen for the life you want to live.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-4 mt-10"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
          >
            <Link
              to="/opportunities"
              data-testid="hero-cta-opportunities"
              className="inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.14em] font-semibold px-8 py-4 rounded-full text-[#0B0C10] bg-gradient-to-r from-[#E6CA65] via-[#C5A880] to-[#9A7B4F] hover:shadow-[0_0_28px_rgba(197,168,128,0.5)] transition-shadow duration-300"
            >
              Explore Curated Opportunities <ArrowRight size={16} />
            </Link>
            <Link
              to="/private-consultation"
              data-testid="hero-cta-consultation"
              className="inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.14em] font-semibold px-8 py-4 rounded-full border border-[rgba(244,243,239,0.35)] text-[#F4F3EF] hover:border-[#E6CA65] hover:text-[#E6CA65] transition-colors duration-300 backdrop-blur-sm"
            >
              Private Consultation
            </Link>
          </motion.div>
          <motion.p
            className="mt-7 text-sm text-[#9DA3AE] font-light"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.35 }}
          >
            Buying from the UK?{" "}
            <Link
              to="/insights/buying-uae-property-as-a-uk-citizen"
              data-testid="hero-uk-guide-link"
              className="text-[#E6CA65] underline underline-offset-4 decoration-[rgba(230,202,101,0.4)] hover:decoration-[#E6CA65] transition-colors"
            >
              Read our step-by-step guide to buying property in Dubai from the UK
            </Link>
          </motion.p>
        </motion.div>
      </section>

      <Marquee />

      <section className="py-16 border-b border-[rgba(197,168,128,0.12)]" data-testid="uae-stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1} className="min-w-0">
              <p className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#E6CA65]">{s.value}</p>
              <p className="text-sm text-[#9DA3AE] font-light mt-3 max-w-[200px] break-words">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24" data-testid="curated-opportunities-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow mb-4">Curated Opportunities</p>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#F4F3EF] max-w-2xl leading-tight">
                This month's selection, chosen for lifestyle and long-term value
              </h2>
              <Link to="/opportunities" data-testid="view-all-opportunities-link" className="text-xs uppercase tracking-[0.2em] text-[#C5A880] hover:text-[#E6CA65] transition-colors inline-flex items-center gap-2">
                View all <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {opportunities.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                <ProjectCard project={p} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0D0F15]" data-testid="lifestyle-destinations-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow mb-4">Lifestyle Destinations</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#F4F3EF] max-w-2xl leading-tight mb-12">
              Three emirates. Three rhythms of life.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {destinations.map((d, i) => (
              <Reveal key={d.slug} delay={i * 0.12}>
                <Link
                  to={`/destinations/${d.slug}`}
                  data-testid={`destination-card-${d.slug}`}
                  className="card-hover group relative block h-[480px] rounded-2xl overflow-hidden border border-[rgba(197,168,128,0.15)]"
                >
                  <img src={d.cardImage} alt={`${d.name} — UAE lifestyle destination`} loading="lazy" className="card-img absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/35 to-transparent" />
                  <div className="absolute bottom-0 p-7">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#E6CA65] mb-2">{d.cardStat}</p>
                    <h3 className="font-display text-3xl text-[#F4F3EF] mb-3">{d.name}</h3>
                    <p className="text-sm text-[#c9cdd6] font-light line-clamp-2">{d.intro}</p>
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C5A880] mt-4 group-hover:gap-3.5 transition-all duration-300">
                      Discover <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="why-acre-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow mb-4">Why Buyers Choose ACRE</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#F4F3EF] max-w-2xl leading-tight mb-14">
              Advisory first. Always.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.12}>
                <div className="card-hover h-full bg-[#13151D] border border-[rgba(197,168,128,0.15)] rounded-2xl p-8" data-testid={`why-acre-${i + 1}`}>
                  <w.icon size={28} className="text-[#E6CA65] mb-6" strokeWidth={1.5} />
                  <h3 className="font-display text-2xl text-[#F4F3EF] mb-4">{w.title}</h3>
                  <p className="text-sm text-[#9DA3AE] font-light leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 overflow-hidden" data-testid="land-jv-teaser">
        <div className="absolute inset-0 bg-[#0E382B]/20" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="eyebrow mb-4">Land, Development & JV</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#F4F3EF] leading-tight mb-6">
              Beyond the unit — into the land itself
            </h2>
            <p className="text-[#9DA3AE] font-light leading-relaxed mb-8 max-w-lg">
              For investors, developers and family offices, ACRE opens the door to plot acquisition, structured
              partnerships and development-led opportunities across the UAE.
            </p>
            <Link
              to="/land-development-jv"
              data-testid="land-jv-teaser-cta"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] font-semibold px-8 py-4 rounded-full border border-[rgba(197,168,128,0.4)] text-[#E6CA65] hover:bg-[rgba(197,168,128,0.08)] transition-colors duration-300"
            >
              Explore Land & JV <Landmark size={16} />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl overflow-hidden border border-[rgba(197,168,128,0.2)]">
              <img src="https://images.pexels.com/photos/1470403/pexels-photo-1470403.jpeg?auto=compress&cs=tinysrgb&q=85" alt="The Oasis by Emaar luxury villa community in Dubai" loading="lazy" className="w-full h-80 object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 border-y border-[rgba(197,168,128,0.12)]" data-testid="developer-partners-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow text-center mb-10">Developer Partnerships</p>
          </Reveal>
          <div className="flex flex-wrap justify-center items-center gap-x-14 gap-y-10 max-w-5xl mx-auto">
            {PARTNERS.map((p) => (
              <img
                key={p.name}
                src={p.logo}
                alt={`${p.name} — ACRE developer partner`}
                title={p.name}
                data-testid={`partner-logo-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                style={p.invert ? { filter: "brightness(0) invert(1)" } : undefined}
                className={`${p.square ? "h-12" : "h-7 sm:h-8"} w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0D0F15]" data-testid="home-testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow mb-4">Client Stories</p>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#F4F3EF] max-w-2xl leading-tight">
                Trusted across continents
              </h2>
              <Link to="/client-stories" data-testid="view-all-stories-link" className="text-xs uppercase tracking-[0.2em] text-[#C5A880] hover:text-[#E6CA65] transition-colors inline-flex items-center gap-2">
                All client stories <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.filter((t) => t.featured).map((t, i) => (
              <Reveal key={t.id} delay={i * 0.12}>
                <figure className="card-hover h-full bg-[#13151D] border border-[rgba(197,168,128,0.15)] rounded-2xl p-8 flex flex-col" data-testid={`testimonial-${t.id}`}>
                  <blockquote className="font-display text-xl italic text-[#F4F3EF] leading-relaxed flex-1">“{t.quote}”</blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-[rgba(197,168,128,0.12)]">
                    <p className="text-sm text-[#E6CA65]">{t.name}</p>
                    <p className="text-xs text-[#9DA3AE] mt-1">{t.meta} · {t.segment}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand testid="home-final-cta" />
    </div>
  );
};

export default Home;
